import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  alpha,
  IconButton,
} from "@mui/material";
import { Package, BarChart3, Settings, InfoIcon, Layers } from "lucide-react";
import ProductItemUnitListRow from "./ProductItemUnitListRow";
import { useGetList } from "../../../../hooks/useGetList";
import { useDispatch, useSelector } from "react-redux";
import buildParams from "../../../../Utils/buildParams";
import { useParams, useSearchParams } from "react-router-dom";
import ProductItemUnitHeader from "./ProductItemUnitHeader";
import {
  clearShowStockBatch,
  clearUnitInfos,
} from "../../../../redux/itemUnit/productItemUnitSlice";
import ProductItemUnitInfoList from "./ProductItemUnitInfoList";
import StockBatchList from "./StockBatchList";

const createDemoData = () => [
  {
    id: 1,
    name: "Orange Juice 125 ml",
    sellingPrice: 2.5,
    purchasePrice: 1.8,
    reOrderQty: 20,
    qty: 45,
    created: "2024-01-15",
  },
  {
    id: 2,
    name: "Orange Juice 250 ml",
    sellingPrice: 4.25,
    purchasePrice: 3.2,
    reOrderQty: 15,
    qty: 32,
    created: "2024-01-20",
  },
  {
    id: 3,
    name: "Apple Juice 125 ml",
    sellingPrice: 2.75,
    purchasePrice: 2.0,
    reOrderQty: 25,
    qty: 18,
    created: "2024-02-01",
  },
  {
    id: 4,
    name: "Apple Juice 500 ml",
    sellingPrice: 5.5,
    purchasePrice: 4.1,
    reOrderQty: 10,
    qty: 8,
    created: "2024-02-10",
  },
  {
    id: 5,
    name: "Mango Juice 125 ml",
    sellingPrice: 3.25,
    purchasePrice: 2.4,
    reOrderQty: 20,
    qty: 55,
    created: "2024-02-15",
  },
  {
    id: 6,
    name: "Mango Juice 250 ml",
    sellingPrice: 5.75,
    purchasePrice: 4.25,
    reOrderQty: 12,
    qty: 28,
    created: "2024-03-01",
  },
  {
    id: 7,
    name: "Grape Juice 500 ml",
    sellingPrice: 6.0,
    purchasePrice: 4.5,
    reOrderQty: 8,
    qty: 5,
    created: "2024-03-05",
  },
  {
    id: 8,
    name: "Mixed Fruit Juice 1 L",
    sellingPrice: 8.5,
    purchasePrice: 6.25,
    reOrderQty: 6,
    qty: 12,
    created: "2024-03-10",
  },
];

const ProductItemUnitList = () => {
  const [data] = useState(createDemoData());
  const filter = useSelector((state) => state.filterItemUnit);
  const { refresh } = useSelector((state) => state.refresh);

  const { id } = useParams();

  const [searchParam, setSearchParam] = useSearchParams();
  const { selectedBranch } = useSelector((state) => state.filterItemUnit);
  const param = buildParams(filter);
  const {
    data: productItemUnits,
    loading,
    refetch,
  } = useGetList(
    `item-unit?relatedItem=${id}`,
    selectedBranch ? true : false,
    Object.keys(param).length > 0 ? param : undefined,
  );

  const dispatch = useDispatch();
  const { showUnitInfos, showStockBatch } = useSelector(
    (state) => state.productItemUnit,
  );

  useEffect(() => {
    if (selectedBranch) {
      refetch();
    }
  }, [refresh, selectedBranch]);

  const handleProductItemUnitTable = () => {
    dispatch(clearUnitInfos());
    dispatch(clearShowStockBatch());
  };

  const handleShowUnitInfos = () => {
    dispatch(clearShowStockBatch());
  };

  return (
    <Paper elevation={2} sx={{ width: "100%", overflow: "hidden" }}>
      <Box sx={{ p: 3, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}>
        <Typography variant="h6" component="div">
          Product Item Units{" "}
          {searchParam.get("item") ? `- ${searchParam.get("item")}` : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage your product item units and inventory
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
          {/* Step 1: Product Item Units */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleProductItemUnitTable}
              sx={{
                width: 40,
                height: 40,
                border: 2,
                borderColor: "primary.main",
                color: "primary.contrastText",
                bgcolor: "primary.main",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              }}
            >
              <Package size={20} />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "primary.main",
              }}
            >
              Product Item Unit
            </Typography>
          </Box>

          {/* Progress Line 1 */}
          <Box
            sx={{
              width: "100px",
              height: 2,
              bgcolor: "primary.main",
              transition: "bgcolor 0.3s ease",
            }}
          />

          {/* Step 2: Inventory */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              disabled={!showUnitInfos?.isShowUnitInfos}
              onClick={handleShowUnitInfos}
              sx={{
                width: 40,
                height: 40,
                border: 2,
                borderColor: showUnitInfos?.isShowUnitInfos
                  ? "primary.main"
                  : "grey.300",
                color: showUnitInfos?.isShowUnitInfos
                  ? "primary.contrastText"
                  : "grey.400",
                bgcolor: showUnitInfos?.isShowUnitInfos
                  ? "primary.main"
                  : "transparent",
                "&:hover": showUnitInfos?.isShowUnitInfos
                  ? {
                      bgcolor: "primary.dark",
                    }
                  : {},
              }}
            >
              <InfoIcon />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: showUnitInfos?.isShowUnitInfos
                  ? "primary.main"
                  : "grey.500",
              }}
            >
              Unit Info
            </Typography>
          </Box>

          {/* Progress Line 2 */}
          <Box
            sx={{
              width: "100px",
              height: 2,
              bgcolor: showUnitInfos?.isShowUnitInfos
                ? "primary.main"
                : "grey.300",
              transition: "bgcolor 0.3s ease",
            }}
          />

          {/* Step 3: Settings */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              disabled={!showStockBatch?.isShowStockBatch}
              sx={{
                width: 40,
                height: 40,
                border: 2,
                borderColor: showStockBatch?.isShowStockBatch
                  ? "primary.main"
                  : "grey.300",
                color: showStockBatch?.isShowStockBatch
                  ? "primary.contrastText"
                  : "grey.400",
                bgcolor: showStockBatch?.isShowStockBatch
                  ? "primary.main"
                  : "transparent",
                "&:hover": showStockBatch?.isShowStockBatch
                  ? {
                      bgcolor: "primary.dark",
                    }
                  : {},
              }}
            >
              <Layers />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: showStockBatch?.isShowStockBatch
                  ? "primary.main"
                  : "grey.500",
              }}
            >
              Stock Batch
            </Typography>
          </Box>
        </Box>
      </Box>

      {showUnitInfos?.isShowUnitInfos && !showStockBatch?.isShowStockBatch ? (
        <>
          <ProductItemUnitInfoList />
        </>
      ) : showUnitInfos?.isShowUnitInfos && showStockBatch?.isShowStockBatch ? (
        <>
          <StockBatchList />
        </>
      ) : (
        <TableContainer>
          <Table stickyHeader aria-label="product item units table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, minWidth: 10 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                  Name
                </TableCell>
                {/* <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                Selling Price
              </TableCell> */}
                {/* <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                Purchase Price
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                Quantity
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                Reorder Qty
              </TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                Stock Status
              </TableCell> */}
                <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>
                  Created
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, minWidth: 120, textAlign: "center" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                // Skeleton loaders for loading state
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Skeleton variant="text" width={40} height={32} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} height={32} />
                    </TableCell>
                    {/* <TableCell>
                    <Skeleton variant="text" width={120} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={120} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} height={32} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={120} height={32} />
                  </TableCell> */}
                    <TableCell>
                      <Skeleton variant="text" width={120} height={32} />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : productItemUnits?.data?.length === 0 ? (
                // Empty state when no data
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        gutterBottom
                      >
                        No product item units found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        There are currently no product item units available. Add
                        your first product item unit to get started.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                // Actual data when available
                productItemUnits?.data?.map((row, index) => (
                  <ProductItemUnitListRow
                    key={row._id}
                    row={row}
                    index={index}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default ProductItemUnitList;
